// One published policy, read from the Policy Centre rather than from the
// static legal copy. Shows the version, the effective date and the content
// fingerprint, so a reader can tell exactly which wording they are looking
// at — and so can ConfirmPH, later.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { api } from "@/lib/api";
import { fmtDate } from "@/lib/platformLegal";
import CachePublicShell from "@/components/CachePublicShell";
import SEO from "@/components/SEO";

export default function PublishedPolicy() {
  const { docType } = useParams();
  const [policy, setPolicy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPolicy(null);
    setError(null);
    api.get(`/legal/policies/${docType}`)
      .then((r) => setPolicy(r.data))
      .catch((err) => setError(
        err?.response?.status === 404
          ? "This policy has not been published."
          : "This policy could not be loaded right now."));
  }, [docType]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"
         data-testid={`published-policy-${docType}`}>
      <CachePublicShell />
      <SEO title={`${policy?.title || "Policy"} — ConfirmPH`} description={policy?.summary} />
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
        ConfirmPH · Legal
      </p>

      {!policy && !error && (
        <p className="mt-8 flex items-center gap-2 text-sm text-slate-500" role="status">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </p>
      )}

      {error && (
        <>
          <h1 className="font-display text-3xl font-bold mt-2">Policy not available</h1>
          <p className="mt-4 text-slate-700" data-testid="published-policy-error">{error}</p>
        </>
      )}

      {policy && (
        <>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2">{policy.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span>Version {policy.version_number}</span>
            <span>Effective {fmtDate(policy.effective_date)}</span>
            <span>{policy.jurisdiction}</span>
          </div>
          {policy.summary && (
            <p className="mt-6 text-slate-700 leading-relaxed">{policy.summary}</p>
          )}
          <div className="mt-8 whitespace-pre-wrap text-slate-800 leading-relaxed"
               data-testid="published-policy-body">
            {policy.body}
          </div>
          <p className="mt-8 text-[11px] text-slate-400 font-mono break-all">
            Content fingerprint: {policy.content_hash}
          </p>
        </>
      )}

      <div className="mt-12 pt-6 border-t border-slate-200 text-sm text-slate-600">
        <Link to="/legal" className="underline">← All policies</Link>
      </div>
    </div>
  );
}
